import { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem, CommandSeparator } from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, ChevronsUpDown, Plus } from "lucide-react";
import { cn } from "@/lib/utils";

export default function HsnCombobox({ value, onChange, onSelect, itemType, className }) {
  const [open, setOpen] = useState(false);
  const [directory, setDirectory] = useState([]);
  const [search, setSearch] = useState("");
  const [customMode, setCustomMode] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    api.get("/hsn-directory").then((r) => setDirectory(r.data)).catch(() => {});
  }, []);

  const filtered = directory.filter((entry) => {
    const typeMatch = !itemType || itemType === "all" || entry.type === (itemType === "goods" ? "goods" : "service");
    if (!search) return typeMatch;
    const q = search.toLowerCase();
    return typeMatch && (
      entry.name.toLowerCase().includes(q) ||
      entry.description.toLowerCase().includes(q) ||
      entry.code.includes(q)
    );
  }).slice(0, 15);

  const selectedEntry = directory.find((e) => e.code === value);

  const handleSelect = (entry) => {
    onChange(entry.code);
    if (onSelect) onSelect(entry);
    setOpen(false);
    setSearch("");
    setCustomMode(false);
  };

  const handleCustom = () => {
    setCustomMode(true);
    setOpen(false);
    setSearch("");
    onChange("");
    if (onSelect) onSelect(null);
  };

  if (customMode) {
    return (
      <div className="flex gap-1.5 items-start">
        <div className="flex-1">
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Enter 6+ digit code"
            data-testid="hsn-custom-input"
            className={cn(
              "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring font-mono",
              className
            )}
            autoFocus
          />
        </div>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={() => { setCustomMode(false); onChange(value); }}
          className="text-xs h-9 px-2 text-[#4338ca]"
          data-testid="hsn-back-to-directory"
        >
          Directory
        </Button>
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid="hsn-combobox-trigger"
          className={cn(
            "w-full justify-between h-9 font-normal text-sm",
            !value && "text-muted-foreground",
            className
          )}
        >
          {selectedEntry ? (
            <span className="flex items-center gap-2 truncate">
              <span className="font-mono text-xs text-[#4338ca]">{selectedEntry.code}</span>
              <span className="truncate">{selectedEntry.name}</span>
            </span>
          ) : value ? (
            <span className="font-mono text-xs">{value}</span>
          ) : (
            "Search HSN/SAC..."
          )}
          <ChevronsUpDown className="ml-auto h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[360px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Type name, code or description..."
            value={search}
            onValueChange={setSearch}
            data-testid="hsn-search-input"
          />
          <CommandList>
            <CommandEmpty className="py-3 text-center text-sm text-[#64748b]">
              No matching HSN/SAC found.
            </CommandEmpty>
            {filtered.length > 0 && (
              <CommandGroup heading="HSN/SAC Directory">
                {filtered.map((entry) => (
                  <CommandItem
                    key={entry.code}
                    value={entry.code}
                    onSelect={() => handleSelect(entry)}
                    data-testid={`hsn-option-${entry.code}`}
                    className="flex items-start gap-2 py-2"
                  >
                    <Check className={cn("h-3.5 w-3.5 mt-0.5 shrink-0", value === entry.code ? "opacity-100" : "opacity-0")} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs font-semibold text-[#4338ca]">{entry.code}</span>
                        <span className="text-sm font-medium truncate">{entry.name}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-[#94a3b8] truncate">{entry.description}</span>
                        <Badge className={cn(
                          "text-[10px] px-1.5 py-0 h-4 shrink-0",
                          entry.gst_rate === 5 ? "bg-green-100 text-green-700" :
                          entry.gst_rate === 40 ? "bg-red-100 text-red-700" :
                          "bg-blue-100 text-blue-700"
                        )}>
                          {entry.gst_rate}%
                        </Badge>
                      </div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            <CommandSeparator />
            <CommandGroup>
              <CommandItem
                onSelect={handleCustom}
                data-testid="hsn-add-custom"
                className="flex items-center gap-2 py-2 text-[#4338ca]"
              >
                <Plus className="h-3.5 w-3.5" />
                <span className="text-sm font-medium">Add Custom HSN/SAC</span>
              </CommandItem>
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
